import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { trpc } from "../../lib/trpc";

interface BulkStockImportProps {
  open: boolean;
  onClose: () => void;
}

// Şablon + beklenen kolonlar (sıra önemli). Önceden dolu: Tür/Kod/Ad/Birim (+ Lokasyon).
const COLUMNS = ["Tür", "Kod", "Ad", "Birim", "Miktar", "Parti No", "Lokasyon Kodu", "Üretim Tarihi", "SKT", "Tedarikçi Kodu"];
const COL_WIDTHS = [14, 16, 34, 8, 10, 18, 16, 14, 12, 16];

// Stok tutulabilen lokasyon türleri (üretim merkezi/hat ve tesis hariç).
const STOCK_LOC_TYPES = new Set(["depo", "raf", "bolge", "mal_kabul", "karantina", "sevkiyat", "scrap"]);

/** Miktar biçimi — TR, en çok 4 ondalık (stok miktarları Decimal(18,4)). */
const nf = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 4 });

export function BulkStockImportDrawer({ open, onClose }: BulkStockImportProps) {
  const qc = useQueryClient();
  const [defaultLocId, setDefaultLocId] = useState("");
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [building, setBuilding] = useState(false);
  const [report, setReport] = useState<Awaited<ReturnType<typeof importMut.mutateAsync>> | null>(null);

  const locsQ = trpc.location.list.useQuery(undefined, { enabled: open, staleTime: 60_000 });
  const templateQ = trpc.inventory.bulkStockTemplate.useQuery(undefined, { enabled: false });
  const importMut = trpc.inventory.bulkStockImport.useMutation();

  const stockLocations = useMemo(
    () => (locsQ.data ?? []).filter((l) => l.isActive && STOCK_LOC_TYPES.has(l.type)),
    [locsQ.data],
  );

  const reset = useCallback(() => {
    setFileName("");
    setRows([]);
    setParseError(null);
    setReport(null);
    importMut.reset();
  }, [importMut]);

  useEffect(() => {
    if (open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Lokasyonlar gelince varsayılanı (ilk depo) otomatik seç.
  useEffect(() => {
    if (open && !defaultLocId && stockLocations.length > 0) {
      const depo = stockLocations.find((l) => l.type === "depo") ?? stockLocations[0]!;
      setDefaultLocId(depo.id);
    }
  }, [open, defaultLocId, stockLocations]);

  const defaultLoc = stockLocations.find((l) => l.id === defaultLocId) ?? null;

  function handleClose() {
    reset();
    onClose();
  }

  async function downloadTemplate() {
    setBuilding(true);
    setParseError(null);
    try {
      const res = await templateQ.refetch();
      if (res.error) { setParseError(`Şablon verisi alınamadı: ${res.error.message}`); return; }
      const cards = res.data ?? [];
      const defCode = defaultLoc?.code ?? "";
      const data = cards.map((c) => ({
        "Tür": c.kindLabel,
        "Kod": c.code,
        "Ad": c.name,
        "Birim": c.uom,
        "Miktar": "",
        "Parti No": "",
        "Lokasyon Kodu": defCode,
        "Üretim Tarihi": "",
        "SKT": "",
        "Tedarikçi Kodu": "",
      }));
      const sheetData = data.length > 0 ? data : [Object.fromEntries(COLUMNS.map((h) => [h, ""]))];
      const ws = XLSX.utils.json_to_sheet(sheetData, { header: COLUMNS });
      ws["!cols"] = COL_WIDTHS.map((wch) => ({ wch }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Stok Yükleme");
      XLSX.writeFile(wb, "STEPERP_stok_parti_sablonu.xlsx");
    } finally {
      setBuilding(false);
    }
  }

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    setParseError(null);
    setReport(null);
    try {
      const ab = await f.arrayBuffer();
      const wb = XLSX.read(ab);
      const sheetName = wb.SheetNames[0];
      if (!sheetName) { setParseError("Dosyada sheet bulunamadı."); return; }
      const sheet = wb.Sheets[sheetName]!;
      const parsed = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: null });
      if (parsed.length === 0) { setParseError("Dosya boş veya başlık satırından sonra veri yok."); return; }
      setRows(parsed);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : String(err));
    }
  }

  async function handleSubmit() {
    if (rows.length === 0) return;
    const result = await importMut.mutateAsync({
      defaultLocationCode: defaultLoc?.code,
      rows: rows as Array<Record<string, string | number | boolean | null>>,
    });
    setReport(result);
    await qc.invalidateQueries();
  }

  // Miktar dolu satırlar (sunucu sayısal doğrulamayı yapar; burada yalnız boş/dolu ayrımı).
  const hasQty = (r: Record<string, unknown>) => { const m = r["Miktar"]; return m != null && String(m).trim() !== ""; };
  const filledCount = useMemo(() => rows.filter(hasQty).length, [rows]);
  const preview = useMemo(() => rows.filter(hasQty).slice(0, 6), [rows]);

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title="Toplu Stok / Parti İçe Aktar"
      subtitle={rows.length > 0 ? `${filledCount} dolu satır${fileName ? ` · ${fileName}` : ""}` : "Şablon indir → doldur → yükle"}
      size="lg"
      footer={
        <>
          <button type="button" className="btn-md ghost" onClick={handleClose} disabled={importMut.isPending}>
            {report ? "Kapat" : "İptal"}
          </button>
          {!report ? (
            <button type="button" className="btn-md primary" onClick={handleSubmit} disabled={rows.length === 0 || importMut.isPending}>
              {importMut.isPending ? "Yükleniyor…" : filledCount > 0 ? `${filledCount} Satırı İçe Aktar` : "İçe Aktar"}
            </button>
          ) : null}
        </>
      }
    >
      {!report ? (
        <>
          {/* Metin, davranışın KENDİSİNİ anlatır: bu ekran artık "ekleme" değil SAYIM yapıyor.
              Eski metin "Parti No lot kodu olur" diyordu ve kullanıcı her yüklemede stoğun
              üzerine eklendiğini fark etmiyordu. */}
          <div className="form-banner-info">
            Şablonda tüm aktif kartlar (Tür · Kod · Ad · Birim) hazır gelir. <b>Miktar = o kalemin SAYILAN toplamı</b>;
            sistem aradaki farkı sayım düzeltmesi olarak yazar — stoğun üzerine <b>eklemez</b>.
            Örnek: sistemde 10.000, şablonda 11.000 → <b>+1.000</b> sayım fazlası.
            Miktarı boş veya 0 olan satırlar atlanır. Parti No / SKT yalnız o kalemin hiç lotu yoksa,
            açılacak yeni lot için kullanılır.
          </div>

          <div className="form-row">
            <div className="form-field">
              <label className="lbl" htmlFor="bsi-loc">Varsayılan Lokasyon</label>
              <select id="bsi-loc" className="form-select" value={defaultLocId} onChange={(e) => setDefaultLocId(e.target.value)} disabled={importMut.isPending}>
                <option value="">— Seçin —</option>
                {stockLocations.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.code})</option>)}
              </select>
              <span className="help">Şablonun "Lokasyon Kodu" sütununa yazılır; satırda boş kalanlar buraya düşer.</span>
            </div>
            <div className="form-field">
              <label className="lbl">Şablon</label>
              <button type="button" className="btn-md secondary" onClick={downloadTemplate} disabled={building || locsQ.isLoading}>
                <Icon name="download" size={14} />{building ? "Hazırlanıyor…" : "Şablonu İndir"}
              </button>
              <span className="help">Tüm aktif kartlar önceden dolu (.xlsx).</span>
            </div>
          </div>

          <div className="form-field">
            <label className="lbl" htmlFor="bsi-file">Doldurulmuş dosyayı seç (.xlsx, .xls, .csv)</label>
            <input id="bsi-file" type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} className="form-input" style={{ padding: "6px 10px", height: "auto" }} disabled={importMut.isPending} />
            <span className="help">
              Kolonlar: <code style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>{COLUMNS.join(" · ")}</code>
            </span>
          </div>

          {parseError ? <div className="form-banner-err">{parseError}</div> : null}

          {rows.length > 0 ? (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: "var(--neutral-700)", marginBottom: 6 }}>
                Önizleme (miktar girilmiş ilk {preview.length} / toplam {filledCount} dolu satır)
              </div>
              {filledCount === 0 ? (
                <div className="form-banner-err">Hiçbir satırda Miktar yok. Şablonu doldurup tekrar yükleyin.</div>
              ) : (
                <div style={{ border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)", overflow: "auto", maxHeight: 240 }}>
                  <table style={{ width: "100%", fontSize: 12, borderCollapse: "separate", borderSpacing: 0 }}>
                    <thead>
                      <tr>
                        {["Tür", "Kod", "Ad", "Miktar", "Parti No", "Lokasyon Kodu"].map((c) => (
                          <th key={c} style={{ textAlign: "left", padding: "6px 10px", background: "var(--neutral-50)", borderBottom: "1px solid var(--neutral-200)", fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--neutral-600)", whiteSpace: "nowrap" }}>{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map((r, i) => (
                        <tr key={i}>
                          {["Tür", "Kod", "Ad", "Miktar", "Parti No", "Lokasyon Kodu"].map((c) => (
                            <td key={c} style={{ padding: "6px 10px", borderBottom: "1px solid var(--neutral-100)", whiteSpace: "nowrap", color: r[c] == null || r[c] === "" ? "var(--neutral-400)" : "var(--neutral-900)" }}>
                              {r[c] == null || r[c] === "" ? "—" : String(r[c])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}

          {importMut.error ? <div className="form-banner-err" style={{ marginTop: 12 }}>{importMut.error.message}</div> : null}
        </>
      ) : (
        <>
          <div style={{
            background: "var(--success-subtle)", border: "1px solid color-mix(in srgb, var(--success) 25%, transparent)",
            borderLeft: "3px solid var(--success)", borderRadius: "var(--radius-md)", padding: "12px 14px", marginBottom: 14, fontSize: 13, color: "var(--success-text)",
          }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Sayım uygulandı</div>
            <div className="mono" style={{ fontSize: 12 }}>
              Toplam {report.total} · Fark yazılan {report.applied} · Değişmeyen {report.unchanged} · Atlanan {report.skipped} · Hatalı {report.errors.length}
            </div>
          </div>

          {/* FARK DÖKÜMÜ — kullanıcı neyin ne kadar değiştiğini görmeden "tamam" dememeli:
              sayım stoğu hem artırır hem AZALTIR, sessiz bir eksiltme fark edilmezdi. */}
          {report.lines.length > 0 ? (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: "var(--neutral-700)", marginBottom: 6 }}>Sayım farkları ({report.lines.length} kalem)</div>
              <div style={{ border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)", overflow: "auto", maxHeight: 260 }}>
                <table style={{ width: "100%", fontSize: 12, borderCollapse: "separate", borderSpacing: 0 }}>
                  <thead>
                    <tr>
                      {["Kod", "Sistem", "Sayılan", "Fark"].map((c, i) => (
                        <th key={c} style={{ textAlign: i === 0 ? "left" : "right", padding: "6px 10px", background: "var(--neutral-50)", borderBottom: "1px solid var(--neutral-200)", fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", color: "var(--neutral-600)" }}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {report.lines.map((l, i) => (
                      <tr key={i}>
                        <td className="mono" style={{ padding: "6px 10px", borderBottom: "1px solid var(--neutral-100)", fontSize: 11.5 }}>{l.code}</td>
                        <td className="mono" style={{ padding: "6px 10px", borderBottom: "1px solid var(--neutral-100)", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{nf.format(l.before)}</td>
                        <td className="mono" style={{ padding: "6px 10px", borderBottom: "1px solid var(--neutral-100)", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{nf.format(l.counted)}</td>
                        {/* İşaret AÇIKÇA yazılır (+/−): yön renkle bırakılırsa eksiltme gözden kaçar. */}
                        <td className="mono" style={{ padding: "6px 10px", borderBottom: "1px solid var(--neutral-100)", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600, color: l.diff > 0 ? "var(--success)" : l.diff < 0 ? "var(--danger)" : "var(--neutral-400)" }}>
                          {l.diff > 0 ? "+" : l.diff < 0 ? "−" : ""}{nf.format(Math.abs(l.diff))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
          {report.errors.length > 0 ? (
            <div>
              <div style={{ fontSize: 12, fontWeight: 500, color: "var(--neutral-700)", marginBottom: 6 }}>Atlanan / Hatalı satırlar ({report.errors.length})</div>
              <div style={{ border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)", overflow: "auto", maxHeight: 300 }}>
                <table style={{ width: "100%", fontSize: 12, borderCollapse: "separate", borderSpacing: 0 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", padding: "6px 10px", background: "var(--neutral-50)", borderBottom: "1px solid var(--neutral-200)", fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", color: "var(--neutral-600)", width: 60 }}>Satır</th>
                      <th style={{ textAlign: "left", padding: "6px 10px", background: "var(--neutral-50)", borderBottom: "1px solid var(--neutral-200)", fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", color: "var(--neutral-600)", width: 140 }}>Kod</th>
                      <th style={{ textAlign: "left", padding: "6px 10px", background: "var(--neutral-50)", borderBottom: "1px solid var(--neutral-200)", fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", color: "var(--neutral-600)" }}>Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.errors.map((e, i) => (
                      <tr key={i}>
                        <td className="mono" style={{ padding: "6px 10px", borderBottom: "1px solid var(--neutral-100)", fontSize: 11.5 }}>{e.row}</td>
                        <td className="mono" style={{ padding: "6px 10px", borderBottom: "1px solid var(--neutral-100)", fontSize: 11.5 }}>{e.code ?? "—"}</td>
                        <td style={{ padding: "6px 10px", borderBottom: "1px solid var(--neutral-100)", color: "var(--danger)" }}>{e.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </>
      )}
    </Drawer>
  );
}
